<template>
    <MktCard is-pricing-card class="pricing-plan" :data-testid="`pricing-default-plan-${index}`">
        <template #title>
            <span :data-testid="`pricing-default-plan-${index}-title`">{{ pricingPlan.title }}</span>
        </template>
        <PrismicRichText :field="pricingPlan.subtitle" class="pricing-plan__subtitle" :data-testid="`pricing-default-plan-${index}-subtitle`" />
        <PrismicRichText
            :field="pricingPlan.description"
            class="pricing-plan__description"
            :data-testid="`pricing-default-plan-${index}-description`"
        />
        <hr />
        <PrismicRichText :field="pricingPlan.content" class="pricing-plan__content" :data-testid="`pricing-default-plan-${index}-content`" />
        <template #footer>
            <div v-if="pricingPlan.ctaUrl || pricingPlan.ctaLink" ref="pricingCard" class="pricing-plan__footer">
                <VJoyButton
                    :href="ctaHref ? $withLocalePrefix(ctaHref) : undefined"
                    :variant="buttonVariant"
                    data-component-source="pricing"
                    rel="nofollow"
                    :data-testid="`pricing-default-plan-${index}-cta`"
                >
                    {{ pricingPlan.ctaLabel }}
                </VJoyButton>
            </div>
        </template>
    </MktCard>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref, useTemplateRef} from 'vue';
    import {PrismicRichText} from '@prismicio/vue';
    import type {PricingSliceDefaultItem} from '@prismicio-types';
    import {MktCard} from '#components';
    import {useComponentTracking} from '#imports';
    import {VJoyButton, type TJoyButtonVariants} from '@maltjoy/core-vue';

    const props = defineProps<{pricingPlan: PricingSliceDefaultItem; index: number}>();

    const ctaHref = computed((): string | undefined => props.pricingPlan.ctaUrl || props.pricingPlan.ctaLink || undefined);

    const pricingCard = useTemplateRef('pricingCard');

    const buttonVariant = ref(props.pricingPlan.ctaVariant === 'primary' ? 'main' : (props.pricingPlan.ctaVariant as TJoyButtonVariants));

    onMounted(() => {
        document.querySelectorAll('.pricing-plan__content strong').forEach((el) => {
            if (el.textContent?.trim() === '*') {
                el.classList.add('ai-sparkle');
            }
        });
    });

    useComponentTracking(pricingCard);
</script>

<style lang="scss">
    .pricing-plan {
        display: flex;
        flex-direction: column;

        text-align: left;

        .card-title {
            font-family: var(--joy-font-family-title);
            font-size: var(--joy-font-size-secondary-200);
            margin-top: 0;
        }

        hr {
            margin: var(--joy-core-spacing-8) 0;
            border: none;
            border-top: 1px solid var(--joy-color-neutral-30);
        }

        &__subtitle {
            strong {
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-400);
                font-weight: var(--joy-font-weight-bold);
            }
        }
        &__description {
            margin-top: var(--joy-core-spacing-6);
        }
        &__content {
            ul {
                padding: 0;
                margin: var(--joy-core-spacing-4) 0;
                li {
                    list-style-type: none;
                    background-position: 0 2px;
                    background-repeat: no-repeat;
                    background-image: url(../check.svg);
                    padding-left: var(--joy-core-spacing-6);
                    margin-bottom: var(--joy-core-spacing-3);
                }

                .ai-sparkle {
                    color: transparent;
                    display: inline-block;
                    width: 19px;
                    height: 20px;
                    background-image: url(https://dam.malt.com/cms-front/ai-sparkle.svg?vh=284103);
                }
            }
        }
        &__footer {
            padding-top: var(--joy-core-spacing-8);
            text-align: center;
        }
    }
</style>
